import React, { useState, useEffect } from "react";
import { IonPage, IonContent, IonHeader, IonItem, IonButton, IonFooter, IonToolbar, IonIcon, IonButtons, IonSpinner, IonInput, IonNote, IonLabel, useIonModal, IonNavLink, useIonViewWillEnter, IonGrid, IonRow, IonCol, IonTitle, IonThumbnail, IonSelect, SelectCustomEvent, IonSelectOption, IonText } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { arrowBackOutline, closeOutline } from 'ionicons/icons';
import './AmountPage.css';
import Avatar from 'react-avatar';
import { useAppDispatch, useAppSelector } from "../../services/redux/hook";
import { useAccountDetailsQuery } from "../../services/api/stellar";
import  { setPaymentDestinationInfo } from "../../services/redux/slices/paymentDestinationInfo/paymentDestinationInfo";
import { Asset, PaymentDestinationInfo } from "../../types/types";
import { shortenAccountId } from '../../services/stellar/stellar-utils'
import SendPage from "../send/SendPage";
import * as stellarApi from "../../services/stellar/horizon/data/data";
import { closeModal } from "../../services/redux/slices/opened_navigation_modal/opened_root_modal";
import useCryptoMode from "../../hooks/useCryptoMode";
import NumberFormat from "react-number-format";
import BigNumber from "bignumber.js";
import { Provider } from "react-redux";
import { store } from "../../services/redux/store";

const Page: React.FC = () => {

    const nav = document.querySelector("ion-nav");
    const [canGoBack, setCanGoBack] = useState(false);

    const [isDestinationFunded, setIsDestinationFunded] = useState(true);
    const supportedAssets: Asset[] = useAppSelector((state) => state.supportedAssets.supportedAssets);

    const paymentInfo: PaymentDestinationInfo = useAppSelector((state) => state.paymentDestinationInfo);

    const selectedAsset: Asset = useAppSelector((state) => state.selectedAsset);

    const [toSendAsset, setToSendAsset] = useState<Asset>(paymentInfo.destination_asset ? paymentInfo.destination_asset : selectedAsset);

    const [assetsToReceiveArray, setAssetsToReceiveArray] = useState<Asset[]>([]);
    const { cryptoMode } = useCryptoMode();

    const close = () => {

        dispatch(closeModal());

    }

    const dispatch = useAppDispatch();

    const { t, i18n } = useTranslation();

    const walletInfo = useAppSelector((state) => state.walletInfo);

    let balances: any;

    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key);

    if (accountDetails && accountDetails.details.id) {

        balances = accountDetails.details.balances;
    }

    const [isSpinner, setIsSpinner] = useState(false);

    const [amount, setAmount] = useState(paymentInfo.destination_amount ? paymentInfo.destination_amount : '');
    const [assetToSend, setAssetToSend] = useState<Asset|undefined>(paymentInfo.destination_asset ? paymentInfo.destination_asset : undefined);

    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    const checkAmount = (value: string) => {


        const checkValue = parseFloat(value);

        if (balances[toSendAsset.identifier]) { // need to check this because the asset modal might show assets the user does not have a trust yet. And therefore no balances will be available

            if (typeof checkValue === 'number' && checkValue > 0 && checkValue <= (balances[toSendAsset.identifier].available)) {

                setIsButtonDisabled(false);
            }

            else {
                setIsButtonDisabled(true);
            }

        }

        else {

            setIsButtonDisabled(true)

        }

    }

    const addAmount = (value: string | undefined | null) => {

        if (value && isDestinationFunded) {

            if (value.indexOf(',') > -1) {
                value = value.replace(',', '.')

            }

            let amount = +parseFloat(value).toFixed(2)

            if (!amount || isNaN(amount)) {
                setAmount('0')
                return
            }

            setAmount(amount.toString());
            const updatedPaymentInfo = { ...paymentInfo }
            updatedPaymentInfo.destination_amount = amount.toString();
            updatedPaymentInfo.destination_asset = toSendAsset;
            dispatch(setPaymentDestinationInfo(updatedPaymentInfo))

            checkAmount(value)

        }
        else {
            setAmount('0');
            checkAmount('0')
        }
    }

    // const setMaxAmount = () => {

    //     const balance = balances[selectedAsset.identifier].available;
    //     setAmount(balance)
    //     setIsButtonDisabled(false)

    // }

    const isAccountFunded = async (account: string) => {

        const isFunded = await stellarApi.isFunded(account);

        return isFunded;
    }


    const goToPaymentReviewPage = async () => {

        setIsSpinner(true);
        const funded = await isAccountFunded(paymentInfo.destination_address as string);

        if (!funded) {

            setIsDestinationFunded(false);
            setIsSpinner(false);
            return;

        }

        setIsSpinner(false);

        nav?.push(() => <SendPage />);
    }

    const getAssetsToReceive = () => {

        const toReceiveAssets: Asset[] = [];

        // If an asset was explicitly set in paymentInfo (from CashAccountPage), only show that asset
        if (paymentInfo.destination_asset){

            toReceiveAssets.push(paymentInfo.destination_asset);
            setAssetsToReceiveArray(toReceiveAssets);
            return;

        }

        // Otherwise, show all available assets (from QuickAction or other flows)
        supportedAssets
            .filter((asset: Asset) => {
                // Ensure the asset is either fiat or crypto based on the mode
                return (asset.type === 'fiat' || (cryptoMode && asset.type === 'crypto'))
            })
            .map((asset: Asset) => {
                // Only include assets that have a balance in the account
                if (accountDetails?.details.balances[asset.identifier]) {
                    toReceiveAssets.push(asset);
                }
            });

        setAssetsToReceiveArray(toReceiveAssets);

    }

    const renderHeader = () => {
        if (canGoBack) {
            return (
                <IonToolbar>
                    <IonNavLink routerDirection={'back'}>
                        <IonButtons slot='start'>
                            <IonButton color='primary'>
                                <IonIcon slot='start' icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonNavLink>
                    <IonTitle> {t('amount.page.send.HEADER') as string}</IonTitle>
                </IonToolbar>
            );
        } else {
            return (
                <IonToolbar>
                    <IonButtons slot='start' onClick={() => { close() }} >
                        <IonButton color='primary'>
                            <IonIcon slot='start' icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{t('amount.page.send.HEADER') as string}</IonTitle>
                </IonToolbar>
            );
        }
    };

    useEffect(() => {

        const init = async () => {
            const goBack = await nav?.canGoBack();
            setCanGoBack(goBack ?? false);
            checkAmount(amount);
        }
        init()

    }, [toSendAsset, canGoBack])

    useEffect(() => {
        getAssetsToReceive();
    }, [cryptoMode, paymentInfo.destination_asset, accountDetails]);

    // Update toSendAsset when assetsToReceiveArray changes
    useEffect(() => {
        if (assetsToReceiveArray.length > 0) {
            // If asset is pre-selected from paymentInfo, use that
            if (paymentInfo.destination_asset) {
                setToSendAsset(paymentInfo.destination_asset);
            } else {
                // Otherwise use the first available asset
                setToSendAsset(assetsToReceiveArray[0]);
            }
        }
    }, [assetsToReceiveArray]);

    const handleSelectEvent = (assetCode: string) => {

        const selectedAsset = supportedAssets.find((asset: Asset) => {
            return asset.code === assetCode;
        });

        if (selectedAsset) {
            setToSendAsset(selectedAsset)
        }
    }

    return (
        <IonPage>

            <IonHeader className="ion-no-border">

                {renderHeader()}

                <IonToolbar style={{ marginTop: "5%", marginBottom: "5%" }}>
                    <IonItem
                        lines="none"
                        hidden={!paymentInfo.destination_username?.name}
                        color={'var(--ion-background-color)'}
                    >
                        <div slot='start'>

                            <Avatar
                                name={`${paymentInfo.destination_username?.name}`}
                                size="50" round={true}
                            />
                        </div>

                        <IonLabel slot="end" className="ion-text-wrap">
                            <h2> {paymentInfo.destination_username?.name}</h2>

                            <IonNote color={'primary'}>{paymentInfo.destination_username?.username}</IonNote>
                        </IonLabel>


                    </IonItem>

                    <IonGrid
                        hidden={paymentInfo.destination_username?.name ? true : false}>
                        <IonRow>
                            <IonCol size="2">
                                <div>

                                    <Avatar
                                        name={t('amount.page.send.STELLAR-ADDRESS') as string}
                                        size="60" round={true}
                                    />

                                </div>
                            </IonCol>
                            <IonCol className="ion-text-end">
                                <div>

                                    <h5> {t('amount.page.send.STELLAR-ADDRESS')}</h5>

                                </div>
                                <div>
                                    <IonNote color={'primary'}>{shortenAccountId(paymentInfo.destination_address ? paymentInfo.destination_address : '')}</IonNote>
                                </div>

                            </IonCol>
                        </IonRow>

                    </IonGrid>


                </IonToolbar>
            </IonHeader>

            <IonContent scrollY={false}>

                <IonGrid className="ion-padding">

                    <IonRow>

                        <IonCol size="6">
                            <IonItem lines="none" color={'var(ion-background-color)'} >
                                <IonThumbnail className="stellarPayAssetImg" slot="start">
                                    <img src={toSendAsset.icon} />
                                </IonThumbnail>
                                {
                                    assetsToReceiveArray.length > 1
                                        ? (
                                            <IonSelect
                                                interface="popover"
                                                value={toSendAsset.code}
                                                onIonChange={(e: SelectCustomEvent) => handleSelectEvent(e.detail.value)}
                                            >
                                                {
                                                    assetsToReceiveArray.map((asset) => {
                                                        return (
                                                            <IonSelectOption
                                                                key={asset.code}
                                                                value={asset.code}
                                                            >
                                                                {asset.code}
                                                            </IonSelectOption>
                                                        )
                                                    })
                                                }

                                            </IonSelect>)
                                        : <h4> {toSendAsset?.code}</h4>
                                }

                            </IonItem>
                        </IonCol>

                        <IonCol >

                            <IonInput

                                className="ion-text-right"
                                label={t('amount.page.input.LABEL') as string}
                                labelPlacement="floating"
                                fill="outline"
                                required={true}
                                inputmode='decimal'
                                value={amount}
                                placeholder={'0'}
                                debounce={900}
                                clearOnEdit={true}
                                type='text'
                                mode='md'
                                onIonInput={(e: any) => addAmount(e.detail.value!)}
                            />

                        </IonCol>

                    </IonRow>

                    <IonRow style={{marginTop:'10%'}} >
                       
            

                            <IonCol className="ion-text-end" >
                                <IonItem color={'var(ion-background-color)'} lines="none" className="amountBalance-item">
                                    <IonLabel>{t('amount.page.available.balance.LABEL')}</IonLabel>
                                    <IonNote color={'primary'} className="amountBalance-note">
                                        <NumberFormat value={
                                            new BigNumber(balances[toSendAsset.identifier].available)
                                                .decimalPlaces(2, BigNumber.ROUND_DOWN).toNumber()}
                                            displayType={'text'} thousandSeparator={true} suffix={" " + toSendAsset.code} decimalSeparator="." fixedDecimalScale={true} />
                                    </IonNote>
                                </IonItem>
                            </IonCol>
                      
                    </IonRow>

                </IonGrid>

                <div hidden={isDestinationFunded}>
                    <IonLabel style={{ marginTop: '5%' }} className="ion-text-center" color={'danger'}>
                        {t('amount.page.invalid.DESTINATION-NOT-FUNDED-ERROR')}
                    </IonLabel>
                </div>

            </IonContent>

            <IonFooter className="ion-no-border">
                <IonToolbar>
                    <IonButton
                        color='primary'
                        type="submit"
                        onClick={() => { goToPaymentReviewPage() }}
                        disabled={isButtonDisabled}
                        expand="block"
                        id="pageFooterButton">
                        {isSpinner ? (<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                            : t('registration.phone.verification.BTN_CONT')}
                    </IonButton>
                </IonToolbar>
            </IonFooter>

        </IonPage>
    )
}

const AmountPage: React.FC = () => (
    <Provider store={store}>
      <Page />
    </Provider>
  );
  
  export default AmountPage;